import { ApolloClient, ApolloProvider, from, HttpLink, InMemoryCache, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { Kind, OperationTypeNode } from 'graphql';
import { createClient } from 'graphql-ws';
import { useMemo } from 'react';

import { useAuthTokenContext } from '@/auth/KeycloakProvider';
import { config } from '@/config';

type Props = {
  children: React.ReactNode;
};

/**
 * MasterclassApolloProvider
 *
 * Apollo Client provider with JWT authentication and WebSocket subscription support.
 * Uses the stable getFreshToken from AuthTokenProvider to inject auth headers.
 */
export function MasterclassApolloProvider({ children }: Props) {
  const { getFreshToken } = useAuthTokenContext();

  const apolloClient = useMemo(() => {
    // Auth link - injects JWT token into HTTP requests
    const authLink = setContext(async (_, { headers }) => {
      const token = await getFreshToken();
      return {
        headers: {
          ...headers,
          authorization: token ? `Bearer ${token}` : '',
        },
      };
    });

    // HTTP link for queries and mutations
    const httpLink = new HttpLink({
      uri: `${config.BACKEND_URL}/graphql`,
    });

    // WebSocket link for subscriptions
    const wsLink = new GraphQLWsLink(
      createClient({
        url: `${config.BACKEND_URL.replace('http', 'ws')}/graphql`,
        connectionParams: async () => {
          const token = await getFreshToken();
          return {
            Authorization: token ? `Bearer ${token}` : '',
          };
        },
        keepAlive: 15000,
        retryAttempts: Infinity,
        shouldRetry: () => true,
      })
    );

    // Split link - routes subscriptions to WebSocket, others to HTTP
    const splitLink = split(
      ({ query }) => {
        const def = getMainDefinition(query);
        return (
          def.kind === Kind.OPERATION_DEFINITION && def.operation === OperationTypeNode.SUBSCRIPTION
        );
      },
      wsLink,
      from([authLink, httpLink])
    );

    return new ApolloClient({
      link: splitLink,
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'cache-and-network',
          nextFetchPolicy: 'cache-first',
        },
      },
    });
  }, [getFreshToken]);

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
}
